import { Button } from '@helvetia/components/Button'
import { OptionSelectorField } from '@helvetia/components/forms/OptionSelector'
import { Col, Row } from '@helvetia/components/layout'
import { MatchMedia } from '@helvetia/components/MatchMedia'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import { Field, Form, Formik } from 'formik'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { QuestionWrapper } from '../../_components/common.styles'
import { LifecycleView } from '../../_components/ConnectComponent'
import { DropDownSelect } from '../../_components/DropDownSelect'
import europeMap from '../../_components/images/map_europe.png'
import onlyCHMap from '../../_components/images/map_only_CH.png'
import { RadioGroup } from '../../_components/RadioGroup'
import { CalculatorStepper } from '../CaculatorStepper'
import { LocationScopePresenter } from './LocationScope.presenter'
import { Image } from './LocationScope.styles'
import { validateFieldForm } from './LocationScope.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

export interface LocationScopeInfoValues {
  locationScope: string
  percentageOutsideCH: string
}

interface Props {
  presenter: LocationScopePresenter
  onSubmit: (values: LocationScopeInfoValues) => void
}

interface State {
  initialValues?: LocationScopeInfoValues
  isEditing: boolean
  isLoading: boolean
  showPercentageOutsideCH: boolean
}

export class LocationScopeSection
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: LocationScopePresenter
  private locationScopeList
  private outsideCHOptions

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.locationScopeList = [
      { text: props.t('step3.insuranceLocation.onlyCH'), value: 'onlyCH' },
      { text: props.t('step3.insuranceLocation.europe'), value: 'europe' },
    ]
    this.outsideCHOptions = [
      { text: '10%', value: 10 },
      { text: '20%', value: 20 },
      { text: '30%', value: 30 },
      { text: '40%', value: 40 },
      { text: '50%', value: 50 },
      { text: '60%', value: 60 },
      { text: '70%', value: 70 },
      { text: '80%', value: 80 },
      { text: '90%', value: 90 },
      { text: '100%', value: 100 },
    ]
    this.state = {
      initialValues: null,
      isEditing: false,
      isLoading: false,
      showPercentageOutsideCH: false,
    }
  }

  setInitialValues = (initialValues, isEditing: boolean) => {
    this.setState({
      initialValues,
      isEditing,
      showPercentageOutsideCH: initialValues.locationScope === 'europe',
    })
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  handleClickContinuar = (values: LocationScopeInfoValues) =>
    this.presenter.onContinue(values)

  handleClickBack = () => this.presenter.onBackClick()

  handleRadioChange = event =>
    this.setState({
      showPercentageOutsideCH: event.target.value === 'europe',
    })

  render() {
    const { t } = this.props
    const { isEditing } = this.state

    if (!this.state.initialValues) return <Spinner size="xl" />

    return (
      <>
        <Row>
          <Col tablet={12} desktop={4} verticalGutter>
            <CalculatorStepper step={3} />
          </Col>

          <Col tablet={12} desktop={8}>
            <Row>
              <Text as="p" color="black" size="l">
                {t('step3.title')}
              </Text>
            </Row>

            <Formik
              initialValues={this.state.initialValues}
              onSubmit={this.handleClickContinuar}
              validate={values => validateFieldForm(values)}
              render={({ setFieldValue, values }) => (
                <Form>
                  {this.state.showPercentageOutsideCH ? (
                    <Image src={europeMap} />
                  ) : (
                    <Image src={onlyCHMap} />
                  )}
                  <Row marginBottom="m">
                    <Col>
                      <Field
                        component={RadioGroup}
                        name="locationScope"
                        defaultSelected={values.locationScope}
                        questionText={t('step3.insuranceLocation.question')}
                        onChange={event => {
                          if (event.target.value === 'onlyCH')
                            setFieldValue('percentageOutsideCH', 0)
                          this.setState({
                            showPercentageOutsideCH:
                              event.target.value === 'europe',
                          })
                        }}
                        radioList={this.locationScopeList}
                      />
                    </Col>
                  </Row>

                  {this.state.showPercentageOutsideCH && (
                    <Row marginTop="m">
                      <QuestionWrapper>
                        <Text as="div" size="s" color="black">
                          {t('step3.outsideCH.question')}
                        </Text>
                      </QuestionWrapper>
                      <Col>
                        <MatchMedia onlyMobile>
                          <Field
                            component={DropDownSelect}
                            defaultSelected={values.percentageOutsideCH}
                            name="percentageOutsideCH"
                            label={t('step3.outsideCH.selector')}
                            options={this.outsideCHOptions}
                            required
                          />
                        </MatchMedia>
                        <MatchMedia tablet>
                          <Field
                            component={OptionSelectorField}
                            name="percentageOutsideCH"
                            optionList={this.outsideCHOptions}
                            required
                          />
                        </MatchMedia>
                      </Col>
                    </Row>
                  )}
                  <Row verticalGutter marginTop="s" marginBottom="m">
                    <Col verticalGutter mobile={12} tablet={3} desktop={3}>
                      <Button primary type="submit">
                        {isEditing ? t('buttons.save') : t('buttons.continue')}
                      </Button>
                    </Col>
                    <Col verticalGutter mobile={12} tablet={3} desktop={3}>
                      <Button grey onClick={this.handleClickBack}>
                        {isEditing ? t('buttons.cancel') : t('buttons.back')}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            />
          </Col>
        </Row>
      </>
    )
  }
}

export const LocationScopeView = withNamespaces()(
  withRouter(LocationScopeSection),
)
